"use client"

import { createContext, useContext, useState, useCallback, useEffect } from "react"
import { type Locale, defaultLocale, getDictionary, t as translate } from "@/lib/i18n"

type I18nContext = {
  locale: Locale
  setLocale: (locale: Locale) => void
  t: (key: string, params?: Record<string, string | number>) => string
}

const Context = createContext<I18nContext>({
  locale: defaultLocale,
  setLocale: () => {},
  t: (key) => key,
})

export function useLocale() {
  return useContext(Context)
}

function getCookie(name: string): string | undefined {
  if (typeof document === "undefined") return undefined
  const match = document.cookie.match(new RegExp(`(?:^|; )${name}=([^;]*)`))
  return match?.[1]
}

function setCookie(name: string, value: string, days: number) {
  const expires = new Date(Date.now() + days * 864e5).toUTCString()
  document.cookie = `${name}=${value}; expires=${expires}; path=/; SameSite=Lax`
}

export function LocaleProvider({
  children,
  initialLocale,
}: {
  children: React.ReactNode
  initialLocale?: Locale
}) {
  // Always initialize with a deterministic value so SSR and client
  // hydration produce the same output.  The cookie is read after
  // hydration inside useEffect to avoid a mismatch.
  const [locale, setLocaleState] = useState<Locale>(initialLocale ?? defaultLocale)

  const dict = getDictionary(locale)

  const setLocale = useCallback((l: Locale) => {
    setLocaleState(l)
    setCookie("locale", l, 365)
    document.documentElement.lang = l
  }, [])

  // After hydration, sync locale from cookie (if set)
  useEffect(() => {
    const cookie = getCookie("locale")
    if (cookie === "en" || cookie === "es") {
      setLocaleState(cookie)
    }
  }, [])

  // Keep lang attribute in sync
  useEffect(() => {
    document.documentElement.lang = locale
  }, [locale])

  const t = useCallback(
    (key: string, params?: Record<string, string | number>) => translate(dict, key, params),
    [dict]
  )

  return (
    <Context.Provider value={{ locale, setLocale, t }}>
      {children}
    </Context.Provider>
  )
}
